/**
 * Unified field renderer - Main export file
 * Re-exports all field rendering utilities and provides a convenience wrapper
 *
 * This file maintains backward compatibility with existing imports while
 * organizing the implementation across focused modules:
 * - field-helpers.ts: Pure utility functions
 * - field-display-renderer.tsx: Display rendering logic
 * - field-input-renderer.tsx: Input rendering logic
 */

import React from "react";

// Re-export all utilities from field-helpers
export {
  isUUID,
  normalizeMultiUserData,
  convertFieldValue,
  createUpdatePayload,
} from "./field-helpers";

// Re-export display renderer
export { renderFieldDisplay, type RenderFieldDisplayProps } from "./field-display-renderer";

// Re-export input renderer
export { renderFieldInput, type RenderFieldInputProps } from "./field-input-renderer";

// Re-export types
export type { SimpleFieldConfig, FieldValue } from "../types";

/**
 * Props for the unified field renderer
 */
export interface UnifiedFieldRendererProps {
  fieldId: string;
  fieldConfig: any; // SimpleFieldConfig
  value: any; // FieldValue
  mode: "display" | "edit";
  onValueChange?: (value: any) => void;
  placeholder?: string;
  fullWidth?: boolean;
  autoFocus?: boolean;
  showIcon?: boolean;
  className?: string;
  disabled?: boolean;
}

// Import the actual renderers for the wrapper function
import { renderFieldDisplay } from "./field-display-renderer";
import { renderFieldInput } from "./field-input-renderer";

/**
 * Main unified field renderer that switches between display and edit modes
 * Convenience wrapper that delegates to the appropriate renderer
 */
export const renderUnifiedField = (props: UnifiedFieldRendererProps): React.ReactNode => {
  if (props.mode === "display") {
    return renderFieldDisplay({
      fieldId: props.fieldId,
      fieldConfig: props.fieldConfig,
      value: props.value,
      showIcon: props.showIcon,
      className: props.className,
      disabled: props.disabled,
    });
  } else {
    return renderFieldInput({
      fieldId: props.fieldId,
      fieldConfig: props.fieldConfig,
      value: props.value,
      onValueChange: props.onValueChange,
      placeholder: props.placeholder,
      fullWidth: props.fullWidth,
      autoFocus: props.autoFocus,
    });
  }
};
